import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import Toast from '../components/Toast';
import ConfirmDialog from '../components/ConfirmDialog';
import Logo from '../components/Logo';
import BurgerMenu from '../components/BurgerMenu';
import { WebSocketStatus } from '../components/WebSocketStatus';

export default function Settings() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' | 'warning' } | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{ title: string; message: string; onConfirm: () => void } | null>(null);

  useEffect(() => {
    // Load user's 2FA status
    const loadUserData = async () => {
      try {
        const response = await api.get('/auth/me');
        setTwoFactorEnabled(response.data.data.twoFactorEnabled || false);
      } catch (error) {
        console.error('Error loading user data:', error);
      }
    };
    loadUserData();
  }, []);

  const toggle2FA = async (enabled: boolean) => {
    setLoading(true);
    try {
      await api.post('/auth/2fa/toggle', { enabled });
      setTwoFactorEnabled(enabled);
      setToast({ 
        message: `Zwei-Faktor-Authentifizierung ${enabled ? 'aktiviert' : 'deaktiviert'}`, 
        type: 'success' 
      });
    } catch (error) {
      console.error('Error toggling 2FA:', error);
      setToast({ message: 'Fehler beim Ändern der 2FA-Einstellung', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = () => {
    setConfirmDialog({
      title: 'Account löschen',
      message: 'Möchtest du deinen Account wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden. Alle deine Listen und Daten werden unwiderruflich gelöscht.',
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          await api.delete('/auth/account');
          setToast({ message: 'Account erfolgreich gelöscht', type: 'success' });
          setTimeout(() => {
            logout();
            navigate('/login');
          }, 1500);
        } catch (error) {
          console.error('Error deleting account:', error);
          setToast({ message: 'Fehler beim Löschen des Accounts', type: 'error' });
        }
      },
    });
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'var(--bg-secondary)' }}>
      <header style={{ 
        backgroundColor: 'var(--bg-primary)',
        padding: '1.5rem',
        borderBottom: '1px solid var(--border-color)'
      }}>
        <div className="container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Logo variant="dark" />
          <BurgerMenu currentPage="settings" />
        </div>
      </header>

      <main className="container" style={{ paddingTop: '2rem', paddingBottom: '2rem', maxWidth: '600px' }}>
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem' }}>
            ⚙️ Einstellungen
          </h2>
          
          <div style={{ 
            padding: '1rem', 
            backgroundColor: 'var(--gray-50)', 
            borderRadius: '0.75rem',
            marginBottom: '1.5rem'
          }}>
            <div style={{ marginBottom: '0.5rem' }}>
              <span style={{ fontSize: '0.875rem', color: 'var(--gray-600)', fontWeight: '500' }}>Name</span>
              <div style={{ fontSize: '1rem', fontWeight: '600', color: 'var(--gray-900)' }}>{user?.name}</div>
            </div>
            <div>
              <span style={{ fontSize: '0.875rem', color: 'var(--gray-600)', fontWeight: '500' }}>E-Mail</span>
              <div style={{ fontSize: '1rem', fontWeight: '600', color: 'var(--gray-900)' }}>{user?.email}</div>
            </div>
          </div>

          <button
            onClick={logout}
            className="btn btn-secondary"
            style={{ width: '100%', marginBottom: '1rem' }}
          >
            🚪 Abmelden
          </button>
        </div>

        <div className="card" style={{ 
          marginBottom: '1.5rem',
          background: twoFactorEnabled 
            ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(5, 150, 105, 0.05) 100%)' 
            : 'var(--bg-primary)',
          border: twoFactorEnabled ? '2px solid var(--success)' : '1px solid var(--border-color)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Decorative background element */}
          {twoFactorEnabled && (
            <div style={{
              position: 'absolute',
              top: '-20px',
              right: '-20px',
              width: '100px',
              height: '100px',
              background: 'radial-gradient(circle, rgba(16, 185, 129, 0.1) 0%, transparent 70%)',
              borderRadius: '50%',
              pointerEvents: 'none'
            }}></div>
          )}
          
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: twoFactorEnabled 
                  ? 'linear-gradient(135deg, var(--success) 0%, #059669 100%)' 
                  : 'linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.5rem',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
              }}>
                {twoFactorEnabled ? '✅' : '🔐'}
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--text-primary)', marginBottom: '0.25rem' }}>
                  Zwei-Faktor-Authentifizierung
                </h3>
                <div style={{ 
                  fontSize: '0.875rem', 
                  color: twoFactorEnabled ? 'var(--success)' : 'var(--text-secondary)',
                  fontWeight: twoFactorEnabled ? '600' : '400'
                }}>
                  {twoFactorEnabled ? '✓ Aktiviert' : 'Deaktiviert'}
                </div>
              </div>
            </div>
            
            <div style={{ 
              padding: '1.25rem',
              backgroundColor: 'var(--bg-secondary)',
              borderRadius: '0.75rem',
              marginBottom: '1.25rem',
              border: '1px solid var(--border-color)'
            }}>
              <p style={{ fontSize: '0.9375rem', color: 'var(--text-secondary)', lineHeight: '1.6', marginBottom: '0.75rem' }}>
                📧 Zusätzlicher Schutz für dein Konto: Bei jedem Login erhältst du einen 6-stelligen Code per Email.
              </p>
              <div style={{ 
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '0.5rem',
                fontSize: '0.8125rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-secondary)' }}>
                  <span>🛡️</span>
                  <span>Mehr Sicherheit</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-secondary)' }}>
                  <span>⚡</span>
                  <span>Schnell per Email</span>
                </div>
              </div>
            </div>

            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              padding: '1rem',
              backgroundColor: 'var(--bg-secondary)',
              borderRadius: '0.75rem',
              border: '2px solid var(--border-color)',
              transition: 'all 0.3s ease'
            }}>
              <div>
                <div style={{ fontWeight: '600', color: 'var(--text-primary)', marginBottom: '0.25rem' }}>
                  2FA {twoFactorEnabled ? 'deaktivieren' : 'aktivieren'}
                </div>
                <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                  {twoFactorEnabled 
                    ? 'Code-Eingabe bei Login entfernen' 
                    : 'Code-Eingabe bei Login erforderlich'}
                </div>
              </div>
              <label style={{ 
                position: 'relative', 
                display: 'inline-block', 
                width: '64px', 
                height: '36px',
                cursor: loading ? 'not-allowed' : 'pointer',
                flexShrink: 0
              }}>
                <input
                  type="checkbox"
                  checked={twoFactorEnabled}
                  onChange={(e) => toggle2FA(e.target.checked)}
                  disabled={loading}
                  style={{ opacity: 0, width: 0, height: 0 }}
                />
                <span style={{
                  position: 'absolute',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: twoFactorEnabled ? 'var(--success)' : 'var(--gray-400)',
                  transition: '0.3s',
                  borderRadius: '36px',
                  boxShadow: twoFactorEnabled ? '0 0 10px rgba(16, 185, 129, 0.3)' : 'none'
                }}>
                  <span style={{
                    position: 'absolute',
                    content: '',
                    height: '28px',
                    width: '28px',
                    left: twoFactorEnabled ? '32px' : '4px',
                    bottom: '4px',
                    backgroundColor: 'var(--bg-primary)',
                    transition: '0.3s',
                    borderRadius: '50%',
                    boxShadow: 'var(--shadow-md)'
                  }} />
                </span>
              </label>
            </div>
          </div>
        </div>

        <div className="card" style={{ 
          borderColor: 'var(--danger)',
          borderWidth: '2px',
          borderStyle: 'solid'
        }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '0.5rem', color: 'var(--danger)' }}>
            ⚠️ Gefahrenbereich
          </h3>
          <p style={{ color: 'var(--gray-600)', marginBottom: '1rem', fontSize: '0.875rem' }}>
            Das Löschen deines Accounts ist unwiderruflich. Alle deine Listen und Daten werden permanent gelöscht.
          </p>
          <button
            onClick={handleDeleteAccount}
            className="btn"
            style={{ 
              width: '100%',
              background: 'linear-gradient(135deg, var(--danger) 0%, #dc2626 100%)',
              color: 'white'
            }}
          >
            🗑️ Account löschen
          </button>
        </div>
      </main>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <WebSocketStatus />

      {confirmDialog && (
        <ConfirmDialog
          title={confirmDialog.title}
          message={confirmDialog.message}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog(null)}
        />
      )}
    </div>
  );
}
